// duplicateModule — Duplicate on a module in a course (PS-43, owner decision
// 2026-10-22: a button, no Alt-drag on the web): the next module, right after
// it, holding the same parts planned and empty — each material part again
// as a planned document of its kind with its title and no brief, so nothing
// is copied that has to be written anew. Port commands one after another;
// the first refusal stops the rest (what was made stays, and is shown).
// Sources and other parts that are not documents are not planned again.

export { duplicateModule, moduleCount };

import { Result } from "@bilda/utils";
import type { ContainerPart } from "../../components/Studio/studioCards.js";
import type { PortResult } from "../ports/result.js";
import type { AppPorts, Library } from "../ports.js";

// How many modules a course holds (unknown course: none).
const moduleCount = (library: Library | null, courseId: string): number => {
    const materials = library?.materials ?? [];
    const parts = materials.find((m) => m.id === courseId)?.parts ?? [];
    return parts.filter(
        (id) =>
            materials.find((m) => m.id === id)?.container?.kind === "module",
    ).length;
};

// What a document is planned as (absent: a page).
type PlannableKind = Parameters<AppPorts["planDocument"]>[3];

// The kind a part is planned as again: what it was planned as, else a quiz
// for a quiz, else a page (the default).
const plannableKindOf = (part: ContainerPart): PlannableKind => {
    switch (part.document?.kind) {
        case "chapter":
        case "page":
        case "quiz":
        case "handout":
            return part.document.kind;
        case "course":
        case "module":
        case undefined:
            return part.template === "quiz" ? "quiz" : undefined;
        default:
            return undefined;
    }
};

const duplicateModule = async (
    ports: AppPorts,
    courseId: string,
    module: ContainerPart,
    title: string,
    // Where the original lies in the course (-1: not known; at the end).
    at: number,
): PortResult<void> => {
    const made = await ports.putInNewModule(title, []);
    if (Result.isErr(made)) return made;
    const id = made.value.materialId;
    const added = await ports.addToModule(courseId, [id]);
    if (Result.isErr(added)) return added;
    if (at >= 0) {
        const moved = await ports.reorderPart(courseId, id, at + 1);
        if (Result.isErr(moved)) return moved;
    }
    return module.parts
        .filter((p) => p.kind === "material")
        .reduce<PortResult<void>>(
            (done, part) =>
                done.then(async (r) => {
                    if (Result.isErr(r)) return r;
                    const planned = await ports.planDocument(
                        id,
                        part.title,
                        "",
                        plannableKindOf(part),
                    );
                    return Result.isErr(planned)
                        ? planned
                        : Result.ok(undefined);
                }),
            Promise.resolve(Result.ok(undefined)),
        );
};
